import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRamadan } from '../context/RamadanContext';

// Define types for storing data in localStorage
type CustomItemDefinition = {
  id: number;
  text: string;
};

type DailyCompletionStatus = {
  [day: number]: {
    [id: number]: boolean;
  }
};

const Checklist: React.FC = () => {
  const { t } = useTranslation();
  const { dayData, updateChecklist, currentDay } = useRamadan();
  const [editableText, setEditableText] = useState<{ [key: number]: string }>({});
  const [customItems, setCustomItems] = useState<CustomItemDefinition[]>([]);
  const [customItemsCompletion, setCustomItemsCompletion] = useState<{[id: number]: boolean}>({});

  // Load all custom item definitions (shared across all days)
  useEffect(() => {
    try {
      const savedCustomItems = localStorage.getItem('ramadanCustomItemsDefinitions');
      if (savedCustomItems) {
        const items: CustomItemDefinition[] = JSON.parse(savedCustomItems);
        setCustomItems(items);
      }
    } catch (error) {
      console.error("Error loading custom item definitions:", error);
    }
  }, []);

  // Load completion status for the current day
  useEffect(() => {
    try {
      const savedCompletionStatus = localStorage.getItem('ramadanCustomItemsCompletion');
      if (savedCompletionStatus) {
        const completionData: DailyCompletionStatus = JSON.parse(savedCompletionStatus);
        
        // Get completion status for the current day or initialize an empty object
        const currentDayStatus = completionData[currentDay] || {};
        setCustomItemsCompletion(currentDayStatus);
      } else {
        // Reset completion status when changing days if no data exists
        setCustomItemsCompletion({});
      }
    } catch (error) {
      console.error("Error loading completion status:", error);
    }
  }, [currentDay]);

  // Save custom item definitions whenever they change
  useEffect(() => {
    if (customItems.length > 0) {
      try {
        localStorage.setItem('ramadanCustomItemsDefinitions', JSON.stringify(customItems));
      } catch (error) {
        console.error("Error saving custom item definitions:", error);
      }
    }
  }, [customItems]);

  // Save completion status whenever it changes
  useEffect(() => {
    try {
      const savedCompletionStatus = localStorage.getItem('ramadanCustomItemsCompletion');
      let completionData: DailyCompletionStatus = savedCompletionStatus 
        ? JSON.parse(savedCompletionStatus) 
        : {};
      
      // Update just the current day's status
      completionData = {
        ...completionData,
        [currentDay]: customItemsCompletion
      };
      
      localStorage.setItem('ramadanCustomItemsCompletion', JSON.stringify(completionData));
    } catch (error) {
      console.error("Error saving completion status:", error);
    }
  }, [customItemsCompletion, currentDay]);

  const handleCheckboxChange = (id: number, checked: boolean) => {
    updateChecklist(id, checked);
  };

  const handleCustomCheckboxChange = (id: number, checked: boolean) => {
    setCustomItemsCompletion(prev => ({
      ...prev,
      [id]: checked
    }));
  };

  const handleTextChange = (id: number, text: string) => {
    setEditableText(prev => ({ ...prev, [id]: text }));
  };

  const handleTextBlur = (id: number) => {
    const newText = editableText[id];
    if (newText && newText.trim() !== '') {
      // Update dynamic checklist items through context
      if (id < 1000) {
        // Find the item in the dayData to update its text
        const item = dayData?.checklist.find(item => item.id === id);
        if (item) {
          // Here we would ideally call an updateChecklistText function
          // For now, we'll just mark it as completed to show some change
          updateChecklist(id, true);
          
          // Store the text change in localStorage as a workaround
          try {
            const savedTexts = localStorage.getItem('ramadanDynamicChecklistTexts') || '{}';
            const parsedTexts = JSON.parse(savedTexts);
            parsedTexts[`${currentDay}_${id}`] = newText;
            localStorage.setItem('ramadanDynamicChecklistTexts', JSON.stringify(parsedTexts));
          } catch (error) {
            console.error("Error saving dynamic checklist text:", error);
          }
        }
      } else {
        // Update custom items text
        setCustomItems(items => 
          items.map(item => 
            item.id === id ? { ...item, text: newText } : item
          )
        );
      }
    }
  };

  // Add a new custom item (visible across all days)
  const addCustomItem = () => {
    // Generate a unique ID starting from 1000 to differentiate from default items
    const maxId = customItems.length > 0 
      ? Math.max(...customItems.map(item => item.id)) 
      : 999;
    const newId = maxId + 1;
    
    setCustomItems([
      ...customItems,
      { id: newId, text: "" }
    ]);
    
    // Focus the new input after rendering
    setTimeout(() => {
      const input = document.getElementById(`item-${newId}`);
      if (input) input.focus();
    }, 100);
  };

  // Load any saved dynamic checklist text changes
  useEffect(() => {
    if (!dayData) return;
    
    try {
      const savedTexts = localStorage.getItem('ramadanDynamicChecklistTexts');
      if (savedTexts) {
        const parsedTexts = JSON.parse(savedTexts);
        const newEditableText = { ...editableText };
        
        // Apply saved texts to the editable text state
        dayData.checklist.forEach(item => {
          const key = `${currentDay}_${item.id}`;
          if (parsedTexts[key]) {
            newEditableText[item.id] = parsedTexts[key];
          }
        });
        
        setEditableText(newEditableText);
      }
    } catch (error) {
      console.error("Error loading dynamic checklist texts:", error);
    }
  }, [dayData, currentDay, editableText]);

  // If dayData is not loaded yet
  if (!dayData) {
    return <div className="animate-pulse">Loading...</div>;
  }

  // Helper function to get the text to display for dynamic items
  const getDynamicItemText = (item: { id: number; text: string; completed: boolean }) => {
    // First check if we have a modified text in our state
    if (editableText[item.id]) {
      return editableText[item.id];
    }
    
    // Otherwise return the original text
    return item.text;
  };

  return (
    <div className="bg-white border border-[#eee] rounded-lg">
      {/* Title with icon */}
      <div className="flex items-center justify-center py-2 space-x-2">
        <div className="bg-[#9eb1db] rounded-lg w-10 h-10 flex items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" viewBox="0 0 20 20" fill="currentColor">
            <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
            <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
          </svg>
        </div>
        <h2 className="text-lg font-bengali font-semibold text-[#616e9e]">
          {t('sections.dailyChecklist')}
        </h2>
      </div>

      {/* Checklist items */}
      <div className="p-2 border-t border-[#eee]">
        {/* Dynamic checklist items - responsive grid layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4">
          {dayData.checklist.map((item) => (
            <div key={item.id} className="flex items-center py-1">
              <div 
                className="w-5 h-5 border border-[#9eb1db] bg-white flex items-center justify-center mr-2 cursor-pointer"
                onClick={() => handleCheckboxChange(item.id, !item.completed)}
              >
                {item.completed && (
                  <div className="w-3 h-3 bg-[#9eb1db]"></div>
                )}
              </div>
              <input
                type="text"
                id={`item-${item.id}`}
                value={getDynamicItemText(item)}
                onChange={(e) => handleTextChange(item.id, e.target.value)}
                onBlur={() => handleTextBlur(item.id)}
                className="font-bengali text-sm text-gray-700 outline-none border-b border-transparent focus:border-[#9eb1db] w-full"
              />
            </div>
          ))}
        </div>
        
        {/* Custom items (shown on all days) - responsive grid layout */}
        {customItems.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 mt-2 pt-2 border-t border-[#eee]">
            {customItems.map((item) => (
              <div key={item.id} className="flex items-center py-1">
                <div 
                  className="w-5 h-5 border border-[#9eb1db] bg-white flex items-center justify-center mr-2 cursor-pointer"
                  onClick={() => handleCustomCheckboxChange(item.id, !customItemsCompletion[item.id])}
                >
                  {customItemsCompletion[item.id] && (
                    <div className="w-3 h-3 bg-[#9eb1db]"></div>
                  )}
                </div>
                <input
                  type="text"
                  id={`item-${item.id}`}
                  value={editableText[item.id] || item.text}
                  onChange={(e) => handleTextChange(item.id, e.target.value)}
                  onBlur={() => handleTextBlur(item.id)}
                  placeholder="আপনার আইটেম লিখুন..."
                  className="font-bengali text-sm text-gray-700 outline-none border-b border-transparent focus:border-[#9eb1db] w-full"
                />
              </div>
            ))}
          </div>
        )}
        
        {/* Add custom item button */}
        <div className="mt-3 flex justify-center">
          <button 
            onClick={addCustomItem}
            className="flex items-center justify-center bg-[#f0f4ff] hover:bg-[#e0e8ff] text-[#616e9e] font-bengali text-sm px-3 py-1.5 rounded-md transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            {t("actions.addCustomItem")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Checklist; 